import { DOMParser as PMDOMParser, DOMSerializer, Node, Schema } from 'prosemirror-model';
import { EditorState } from 'prosemirror-state';
import { EditorView } from 'prosemirror-view';
import { updateYFragment } from 'y-prosemirror';
import { WebsocketProvider } from 'y-websocket';
import * as Y from 'yjs';

import { transformListItemLikeElementsIntoLists } from './utilsList';

const EDITOR_CONTAINER_CLASSNAME = 'editor-container';
export const getEditorWidth = () =>
  document.querySelector('.ProseMirror')?.getBoundingClientRect().width;

export const getEditorContainerClientRect = () =>
  document.querySelector(`.${EDITOR_CONTAINER_CLASSNAME}`)?.getBoundingClientRect();

export const getEditorContainerInnerWidth = () =>
  document.querySelector(`.${EDITOR_CONTAINER_CLASSNAME}`)?.clientWidth;

const fetchHtmlAsText = async (url: string) => {
  return await (await fetch(url)).text();
};

export const importEditorFromHTML = async (fileUrl: string, editorView: EditorView) => {
  let htmlString, pmDoc;
  try {
    htmlString = await fetchHtmlAsText(fileUrl);
    htmlString = htmlString.replaceAll(
      'src="',
      `src="${fileUrl.replace(fileUrl.split('/').pop()!, '')}`,
    );
  } catch (e) {
    throw new Error('[importEditorFromHTML] problem parsing the HTML file');
  }

  try {
    const htmlDoc = new DOMParser().parseFromString(htmlString, 'text/html');
    // The Resultant HTML will contain an container div in the body. We want to remove that div
    // and shift all of it's children under the body tag.
    const htmlDocElement = htmlDoc.body.children[0];
    // @ts-ignore
    htmlDoc.body.append(...htmlDocElement.childNodes);
    htmlDocElement.remove();
    transformListItemLikeElementsIntoLists(htmlDoc);
    pmDoc = PMDOMParser.fromSchema(editorView.state.schema).parse(htmlDoc.body);
  } catch (e) {
    throw new Error('[importEditorFromHTML] problem generating the prosemirror doc');
  }

  try {
    let newState = EditorState.create({
      schema: editorView.state.schema,
      doc: pmDoc,
      plugins: editorView.state.plugins,
    });
    editorView.updateState(newState);
    return true;
  } catch (e) {
    throw new Error('[importEditorFromHTML] problem inserting the prosemirror doc into the state');
  }
};

export const getHTMLFromDoc = (doc: Node, schema: Schema) => {
  const htmlValue = DOMSerializer.fromSchema(schema).serializeFragment(doc.content);

  const temporaryDocument = document.implementation.createHTMLDocument();
  const container = temporaryDocument.createElement('div');

  container.appendChild(htmlValue);

  // Convert font-semibold spans to proper strong tags
  const htmlString = container.innerHTML === '<p></p>' ? '' : container.innerHTML;
  return htmlString
    .replace(/<span class="font-semibold">/g, '<strong>')
    .replace(/<\/span>/g, '</strong>');
};

export const getPMNodeCount = (editorView: EditorView) => {
  return editorView?.state?.doc?.childCount;
};

export { transformListItemLikeElementsIntoLists } from './utilsList';

function isEmptyParagraphElement(el: any): boolean {
  if (!el?.nodeName || el.nodeName !== 'paragraph') {
    return false;
  }
  if (el.length === 0) {
    return true;
  }
  // Allow only empty text children (no nested elements / non-empty text).
  return el.toArray().every((child: any) => {
    if (child?.nodeName) {
      return false;
    }
    return !child?.length;
  });
}

/**
 * True when the fragment has no blocks, or only empty paragraph placeholders.
 * After Yjs sync, new lessons often arrive with a single empty paragraph — that should
 * still get the default empty video block for microlearning onboarding.
 */
function isEmptyOrPlaceholderOnly(fragment: Y.XmlFragment): boolean {
  const children = fragment.toArray();
  if (children.length === 0) {
    return true;
  }
  return children.every(isEmptyParagraphElement);
}

function isPmDocEmptyOrPlaceholderOnly(doc: Node): boolean {
  if (doc.childCount === 0) {
    return true;
  }
  let onlyPlaceholders = true;
  doc.forEach((node) => {
    if (node.type.name !== 'paragraph' || node.textContent.length > 0) {
      onlyPlaceholders = false;
    }
  });
  return onlyPlaceholders;
}

/**
 * Seed a Yjs document with a default video node if the fragment is empty
 * (or only has empty paragraph placeholders from sync).
 * Uses updateYFragment so placeholder paragraphs are replaced, not merged.
 * @param schema - ProseMirror schema containing a 'video' node type
 * @param yProvider - Yjs provider instance
 * @param fragmentName - Name of the XmlFragment, usually 'prosemirror'
 */

export function seedYDocWithDefaultVideo(
  schema: Schema,
  yProvider: WebsocketProvider | undefined,
  fragmentName: string = 'prosemirror',
) {
  if (!yProvider) return;

  const type = yProvider.doc.getXmlFragment(fragmentName);

  if (!isEmptyOrPlaceholderOnly(type)) {
    // Real content already present (including a video) — do not insert.
    return;
  }

  const videoNode = schema.nodes.video?.createAndFill?.() ?? schema.nodes.video?.create();
  if (!videoNode) return;

  const pmDocNode = schema.node('doc', null, [videoNode]);
  try {
    // Reconcile the live fragment to a video-only doc (replaces empty paragraph placeholders).
    updateYFragment(yProvider.doc, type, pmDocNode, new Map());
  } catch (err) {
    console.error('Error seeding default video into Y.Doc', err);
  }
}

/**
 * After the ProseMirror view exists, replace empty/placeholder doc with a default video
 * block. Needed because y-prosemirror can re-materialize an empty paragraph on bind,
 * undoing a pre-view Y.Doc seed.
 */
export function replaceEditorPlaceholderWithDefaultVideo(view: EditorView, schema: Schema) {
  const { doc } = view.state;
  let hasVideo = false;
  doc.forEach((node) => {
    if (node.type.name === 'video') {
      hasVideo = true;
    }
  });
  if (hasVideo || !isPmDocEmptyOrPlaceholderOnly(doc)) {
    return;
  }

  const videoNode = schema.nodes.video?.createAndFill?.() ?? schema.nodes.video?.create();
  if (!videoNode) return;

  const tr = view.state.tr.replaceWith(0, doc.content.size, videoNode);
  view.dispatch(tr);
}
